import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Globe, Share2, Smartphone, Gamepad2, Bell } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { shareNicaBros } from "@/lib/share";
import { pushSupported, isPushSubscribed, enablePush, disablePush } from "@/lib/push";

export default function TitleSettings({ onClose, onSaveToPhone }) {
  const { t, lang, setLang } = useLang();
  const { user } = useAuth();
  const [showHelp, setShowHelp] = useState(false);
  const [remindersOn, setRemindersOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { isPushSubscribed().then(setRemindersOn); }, []);

  const toggleReminders = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (remindersOn) {
        await disablePush();
        setRemindersOn(false);
        toast.message(t("remindersDisabled"));
      } else {
        const r = await enablePush(user?.id);
        if (r.ok) { setRemindersOn(true); toast.success(t("remindersEnabled")); }
        else if (r.reason === "denied") toast.error(t("remindersDenied"));
        else toast.message(t("remindersUnsupported"));
      }
    } catch (e) {
      toast.error("Error");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const res = await shareNicaBros({ text: t("boardShareText") });
    if (res === "shared") toast.success(t("rankShared"));
    else if (res === "copied") toast.success(t("rankCopied"));
  };

  const Row = ({ icon: Icon, label, children, onClick, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 transition-colors rounded-xl px-4 py-2.5 text-left"
    >
      <Icon size={18} strokeWidth={2.5} className="text-[#FFEB3B] shrink-0" />
      <span className="font-vt text-lg text-white flex-1">{label}</span>
      {children}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[92] overflow-y-auto overscroll-contain flex items-start justify-center bg-black/85 p-3 safe-x safe-t safe-b" data-testid="title-settings-modal">
      <div className="pixel-card bg-[#141414] w-full max-w-sm p-4 pop-in relative overflow-y-auto max-h-full my-auto">
        <button data-testid="title-settings-close-btn" onClick={onClose} className="pixel-btn bg-black/55 text-white p-2 absolute top-2.5 right-2.5" aria-label="Close">
          <X size={16} strokeWidth={2.5} />
        </button>
        <h2 className="font-pixel text-base text-[#FFEB3B] pixel-stroke text-center mb-3">{t("titleSettings")}</h2>

        <div className="flex flex-col gap-2">
          {/* Language */}
          <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-2.5">
            <Globe size={18} strokeWidth={2.5} className="text-[#FFEB3B] shrink-0" />
            <span className="font-vt text-lg text-white flex-1">{t("language")}</span>
            <div className="flex gap-1">
              <button data-testid="settings-lang-es" onClick={() => setLang("es")} className={`font-pixel text-[9px] px-2.5 py-1.5 rounded ${lang === "es" ? "bg-[#0067C6] text-white" : "bg-white/10 text-white/70"}`}>ES</button>
              <button data-testid="settings-lang-en" onClick={() => setLang("en")} className={`font-pixel text-[9px] px-2.5 py-1.5 rounded ${lang === "en" ? "bg-[#0067C6] text-white" : "bg-white/10 text-white/70"}`}>EN</button>
            </div>
          </div>

          <Row icon={Share2} label={t("shareGame")} onClick={share} testid="settings-share-btn" />
          <Row icon={Smartphone} label={t("saveToPhone")} onClick={() => { onClose(); onSaveToPhone(); }} testid="settings-savephone-btn" />

          {pushSupported() && (
            <button data-testid="settings-reminders-toggle" onClick={toggleReminders} disabled={busy}
              className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 transition-colors rounded-xl px-4 py-2.5 text-left">
              <Bell size={18} strokeWidth={2.5} className="text-[#FFEB3B] shrink-0" />
              <span className="font-vt text-lg text-white flex-1">{t("dailyReminders")}</span>
              <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${remindersOn ? "bg-[#4CAF50] justify-end" : "bg-white/20 justify-start"}`}>
                <span className="w-5 h-5 rounded-full bg-white block" />
              </span>
            </button>
          )}

          <Row icon={Gamepad2} label={t("howToPlay")} onClick={() => setShowHelp((v) => !v)} testid="settings-howtoplay-btn" />
          {showHelp && (
            <p className="font-vt text-base text-white/80 leading-tight bg-black/30 rounded-xl px-4 py-3" data-testid="settings-help-text">
              {t("settingsHelp")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
